"use client";

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { formatCredits } from "./escape-hud";
import type { Direction } from "@llmgateway/shared/sandbox-escape";
export interface TraceEntry {
  step: number;
  move: Direction;
  thought: string;
  event: string;
  /** False when the reply had to be salvaged, or had no move in it at all. */
  understood: boolean;
  cost: number;
}
const MOVE_GLYPH: Record<Direction, string> = {
  up: "↑",
  down: "↓",
  left: "←",
  right: "→",
  wait: "·"
};
interface EscapeTraceProps {
  entries: TraceEntry[];
  thinking: boolean;
  model: string;
  className?: string;
}
export function EscapeTrace(t0) {
  const $ = _c(28);
  const {
    entries,
    thinking,
    model,
    className
  } = t0;
  const scrollRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth"
      });
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== entries.length || $[2] !== thinking) {
    t2 = [entries.length, thinking];
    $[1] = entries.length;
    $[2] = thinking;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== className) {
    t3 = cn("flex min-h-0 flex-col rounded-xl border border-emerald-500/20 bg-[#04070a]/90", className);
    $[4] = className;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="font-mono text-[10px] tracking-[0.2em] text-emerald-500/60 uppercase">Model trace</span>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== model) {
    t5 = <div className="flex items-center justify-between border-b border-emerald-500/15 px-4 py-3">{t4}<span className="max-w-[55%] truncate font-mono text-[10px] text-emerald-500/40">{model}</span></div>;
    $[7] = model;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== entries.length || $[10] !== thinking) {
    t6 = entries.length === 0 && !thinking ? <p className="font-mono text-[11px] leading-relaxed text-emerald-500/40">Awaiting first inference. Every line below is one real API call, billed to your credits.</p> : null;
    $[9] = entries.length;
    $[10] = thinking;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== entries) {
    t7 = entries.map(_temp);
    $[12] = entries;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = <AnimatePresence initial={false}>{t7}</AnimatePresence>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== thinking) {
    t9 = thinking ? <motion.li initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="flex items-center gap-3"><span className="w-6 shrink-0" /><span className="font-mono text-[11px] text-emerald-400/70">thinking<motion.span animate={{
          opacity: [0.2, 1, 0.2]
        }} transition={{
          duration: 1.2,
          repeat: Infinity
        }}>…</motion.span></span></motion.li> : null;
    $[16] = thinking;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t8 || $[19] !== t9) {
    t10 = <ul className="flex flex-col gap-3">{t8}{t9}</ul>;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t6) {
    t11 = <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-3">{t6}{t10}</div>;
    $[21] = t10;
    $[22] = t6;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t3 || $[26] !== t5) {
    t12 = <div className={t3}>{t5}{t11}</div>;
    $[24] = t11;
    $[25] = t3;
    $[26] = t5;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  return t12;
}
function _temp(entry, index) {
  return <motion.li key={index} initial={{
    opacity: 0,
    x: -8
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.2
  }} className="flex gap-3"><span className="mt-[2px] w-6 shrink-0 text-right font-mono text-[10px] text-emerald-500/35 tabular-nums">{entry.step}</span><div className="min-w-0 flex-1"><div className="flex items-baseline gap-2"><span className={cn("font-mono text-sm leading-none font-bold", entry.understood ? "text-emerald-300" : "text-amber-400")}>{MOVE_GLYPH[entry.move]}</span><span className="font-mono text-[10px] tracking-wider text-emerald-500/45 uppercase">{entry.move}</span>{!entry.understood ? <span className="rounded-sm bg-amber-500/15 px-1.5 py-[1px] font-mono text-[9px] text-amber-300">no valid move</span> : null}<span className="ml-auto font-mono text-[9px] text-emerald-500/30">{formatCredits(entry.cost)}</span></div>{entry.thought ? <p className="mt-1 font-mono text-[11px] leading-relaxed text-emerald-100/70 italic">“{entry.thought}”</p> : null}<p className="mt-1 font-mono text-[10px] leading-relaxed text-emerald-500/40">{entry.event}</p></div></motion.li>;
}